import React from "react";
import { motion } from "framer-motion";
import { PROBLEMS } from "@/data/site";
import { SITE } from "@/constants/testIds";

// Editorial supporting visuals — production-floor stills (Unsplash, cinematic crops)
const VISUALS = [
  { src: "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=900&q=70", ratio: "1 / 1" },
  { src: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=900&q=70", ratio: "3 / 4" },
  { src: "https://images.unsplash.com/photo-1604079628040-94301bb21b91?auto=format&fit=crop&w=900&q=70", ratio: "1 / 1" },
];

const FALLBACK = "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&w=900&q=70";

export default function WhatWeSolve() {
  return (
    <section data-testid={SITE.whatWeSolve} className="bg-bone text-ink py-20 md:py-24 lg:py-28">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-12 gap-y-12 gap-x-8 items-start">
          {/* Left column — eyebrow / headline / sub / image trio */}
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow text-ink/60 mb-5">◐ What We Solve</p>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter font-medium leading-[0.95]">
              Most briefs are<br/>solutions in disguise.
            </h2>
            <p className="mt-8 text-ink/70 text-base md:text-lg leading-relaxed max-w-md">
              We don&rsquo;t start with services. We start with the business problems we&rsquo;ve been hired to solve, again and again — because they are the only ones that matter.
            </p>

            <div className="mt-12 grid grid-cols-3 gap-3 max-w-lg">
              {VISUALS.map((v, i) => (
                <motion.div
                  key={v.src}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="relative overflow-hidden bg-ink/10"
                  style={{ aspectRatio: v.ratio }}
                >
                  <img
                    src={v.src}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover grayscale contrast-110 hover:grayscale-0 transition-all duration-700"
                    loading="lazy"
                    onError={(e) => { if (e.currentTarget.src !== FALLBACK) e.currentTarget.src = FALLBACK; }}
                  />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right column — problem list, baseline-aligned with left headline */}
          <div className="col-span-12 md:col-span-7 md:pl-12 lg:pl-20">
            <ul className="border-t border-ink/15">
              {PROBLEMS.map((p, i) => (
                <motion.li
                  key={p.k}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.45 }}
                  className="group border-b border-ink/15 py-6 md:py-7 flex items-baseline gap-5 md:gap-8 cursor-default"
                >
                  <span className="font-mono text-[11px] tracking-[0.18em] text-ink/45 w-10 shrink-0 pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 font-display text-2xl md:text-3xl lg:text-[34px] tracking-tight text-ink leading-[1.1] transition-colors group-hover:text-relatable">
                    {p.t}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
